import { FriendlyCaptchaSDK } from '@friendlycaptcha/sdk';

declare global {
  interface Window {
    captain: string;
    mycaptain: boolean;
  }
}

export type CaptchaSessionState = {
  token?: string;
  used: boolean;
  createdAt?: number;
};

export type CaptchaController = {
  attach(): void;
  startIfNeeded(): void;
  getToken(): string | undefined;
  hasUsableToken(): boolean;
  markTokenUsed(): void;
  reset(): void;
  destroy(): void;
};

type CaptchaControllerOptions = {
  form: HTMLFormElement;
  mountElement: HTMLElement;
  emailInput?: HTMLInputElement | null;
  sitekey: string;
  locale: 'de' | 'en';
  storageKey?: string;
  onComplete?: () => void;
  onExpire?: () => void;
  onError?: (error?: unknown) => void;
  onShowWidget?: () => void;
  onHideWidget?: () => void;
  enabled: boolean;
};

type InitCaptchaForFormOptions = {
  form?: HTMLFormElement | null;
  mountElement?: HTMLElement | null;
  emailInput?: HTMLInputElement | null;
  locale: 'de' | 'en';
  sitekey?: string;
  enabled?: boolean;
  onComplete?: () => void;
  onExpire?: () => void;
  onError?: (error?: unknown) => void;
  onShowWidget?: () => void;
  onHideWidget?: () => void;
};

type InitCaptchaForFormResult = {
  controller?: CaptchaController;
  requireCaptchaOnSubmit: boolean;
};

const STORAGE_KEY = 'captainCaps.session.store';
const DEFAULT_TOKEN_TTL_MS = 10 * 60 * 1000;
const STATE_EVENT_NAME = 'captaincaps:statechange';

type CaptchaStateChangeDetail = {
  storageKey: string;
  state: CaptchaSessionState;
};

let sdkInstance: FriendlyCaptchaSDK | undefined;

function getSdk() {
  if (!sdkInstance) {
    sdkInstance = new FriendlyCaptchaSDK();
  }

  return sdkInstance;
}

function readState(storageKey: string): CaptchaSessionState {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) {
      return { used: false };
    }

    const parsed = JSON.parse(raw) as CaptchaSessionState;
    const used = Boolean(parsed.used);
    const createdAt = typeof parsed.createdAt === 'number' ? parsed.createdAt : parsed.token ? Date.now() : undefined;

    if (parsed.token && typeof parsed.createdAt !== 'number') {
      writeState(storageKey, {
        token: parsed.token,
        used,
        createdAt
      });
    }

    return {
      token: parsed.token,
      used,
      createdAt
    };
  } catch (_error) {
    return { used: false };
  }
}

function writeState(storageKey: string, state: CaptchaSessionState): void {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(state));
  } catch (_error) {}
}

function clearState(storageKey: string): void {
  try {
    sessionStorage.removeItem(storageKey);
  } catch (_error) {}
}

function isExpired(state: CaptchaSessionState): boolean {
  if (!state.createdAt) {
    return true;
  }

  return Date.now() - state.createdAt > DEFAULT_TOKEN_TTL_MS;
}

function hasUsableState(state: CaptchaSessionState): boolean {
  return Boolean(state.token && !state.used && !isExpired(state));
}

function emitStateChange(detail: CaptchaStateChangeDetail): void {
  window.dispatchEvent(new CustomEvent(STATE_EVENT_NAME, { detail }));
}

export function isCaptchaEnabled(): boolean {
  const isProdHost = window.location.host.includes('ottonova.de');
  return isProdHost || Boolean(window.mycaptain);
}

function getCaptchaUrlParam(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('captcha');
}

export function createCaptchaController(options: CaptchaControllerOptions): CaptchaController {
  const storageKey = options.storageKey || STORAGE_KEY;

  // URL param bypass: ?captcha=xxx auto-fills the solution, skips widget, shows submit button
  const urlCaptchaToken = getCaptchaUrlParam();
  if (urlCaptchaToken) {
    const bypassState: CaptchaSessionState = { token: urlCaptchaToken, used: false, createdAt: Date.now() };
    writeState(storageKey, bypassState);
    emitStateChange({ storageKey, state: bypassState });

    // Immediately trigger the UI swap: hide captcha mount, show submit button
    options.mountElement.classList.add('is-hidden');
    queueMicrotask(() => {
      options.onHideWidget?.();
    });

    return {
      attach() {},
      startIfNeeded() {},
      getToken() {
        return urlCaptchaToken;
      },
      hasUsableToken() {
        return true;
      },
      markTokenUsed() {
        const state = readState(storageKey);
        if (state.token) {
          const nextState = { ...state, used: true };
          writeState(storageKey, nextState);
          emitStateChange({ storageKey, state: nextState });
        }
      },
      reset() {
        // Re-seed the URL param token for the next submission
        const freshState: CaptchaSessionState = { token: urlCaptchaToken, used: false, createdAt: Date.now() };
        writeState(storageKey, freshState);
        emitStateChange({ storageKey, state: freshState });
      },
      destroy() {}
    };
  }

  if (!options.enabled) {
    // On dev environments (no captcha loaded), seed a placeholder token
    // so forms immediately see a usable token and show the submit button.
    const devToken = 'DEV_BYPASS_TOKEN_CAPTCHA_SOLUTION';
    const existingState = readState(storageKey);
    if (!hasUsableState(existingState)) {
      const devState: CaptchaSessionState = { token: devToken, used: false, createdAt: Date.now() };
      writeState(storageKey, devState);
      emitStateChange({ storageKey, state: devState });
    }

    // Immediately trigger the UI swap: hide captcha mount, show submit button
    options.mountElement.classList.add('is-hidden');
    queueMicrotask(() => {
      options.onHideWidget?.();
    });

    return {
      attach() {},
      startIfNeeded() {},
      getToken() {
        return devToken;
      },
      hasUsableToken() {
        return true;
      },
      markTokenUsed() {
        const state = readState(storageKey);
        if (state.token) {
          const nextState = { ...state, used: true };
          writeState(storageKey, nextState);
          emitStateChange({ storageKey, state: nextState });
        }
      },
      reset() {
        // Re-seed a fresh placeholder token for the next submission
        const devState: CaptchaSessionState = { token: devToken, used: false, createdAt: Date.now() };
        writeState(storageKey, devState);
        emitStateChange({ storageKey, state: devState });
      },
      destroy() {}
    };
  }

  let widget: any;
  let started = false;
  let isDestroyed = false;
  let attachRequested = false;
  let cachedTokenPlaceholder: HTMLElement | undefined;
  let removeModalShowListener = () => {};
  let removeInteractionListeners = () => {};
  let manualStartPending = false;
  let thisFormUsedToken = false;

  const getState = () => readState(storageKey);
  const notifyStateChange = (state?: CaptchaSessionState) => emitStateChange({ storageKey, state: state ?? getState() });

  let lastHasUsableToken: boolean | undefined;

  const scheduleInitialization = () => {
    if (widget || isDestroyed) {
      return;
    }

    removeModalShowListener();
    removeModalShowListener = () => {};

    const modal = options.form.closest<HTMLElement>('.modal');

    const onModalShow = () => {
      removeModalShowListener();
      removeModalShowListener = () => {};
      initializeWidget();
    };

    if (!modal) {
      initializeWidget();
      return;
    }

    if (modal.classList.contains('is-open')) {
      onModalShow();
      return;
    }

    modal.addEventListener('modalShow', onModalShow, { once: true });
    removeModalShowListener = () => {
      modal.removeEventListener('modalShow', onModalShow);
    };
  };

  const syncUiWithState = (state: CaptchaSessionState) => {
    const hasToken = hasUsableState(state);

    if (lastHasUsableToken === hasToken) {
      return;
    }

    lastHasUsableToken = hasToken;

    if (hasToken) {
      started = true;
      showCachedTokenUI();
      options.onHideWidget?.();
      return;
    }

    started = false;
    removeCachedTokenUI();

    // Don't re-show the widget if THIS controller's form consumed the token.
    // Other controllers (other forms) will re-show their widget normally.
    if (state.used && thisFormUsedToken) {
      return;
    }

    options.onShowWidget?.();

    if (widget) {
      widget.reset?.();
      return;
    }

    if (attachRequested) {
      scheduleInitialization();
    }
  };

  const handleStateChange = (event: Event) => {
    const customEvent = event as CustomEvent<CaptchaStateChangeDetail>;
    if (!customEvent.detail || customEvent.detail.storageKey !== storageKey) {
      return;
    }

    syncUiWithState(customEvent.detail.state);
  };

  window.addEventListener(STATE_EVENT_NAME, handleStateChange);
  syncUiWithState(getState());

  const saveToken = (token: string) => {
    const nextState = { token, used: false, createdAt: Date.now() };
    writeState(storageKey, nextState);
    syncUiWithState(nextState);
    notifyStateChange(nextState);
  };

  function showCachedTokenUI() {
    if (cachedTokenPlaceholder) {
      return;
    }

    const el = document.createElement('div');
    el.className = 'frc-captcha-cached';
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('data-label', options.locale === 'en' ? 'Verified' : 'Verifiziert');
    el.innerHTML =
      '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="3 9 7 13 13 4"/></svg>';
    options.mountElement.appendChild(el);
    cachedTokenPlaceholder = el;
  }

  function removeCachedTokenUI() {
    cachedTokenPlaceholder?.remove();
    cachedTokenPlaceholder = undefined;
  }

  const start = () => {
    if (started || isDestroyed) {
      return;
    }

    if (hasUsableState(getState())) {
      started = true;
      return;
    }

    if (!widget) {
      return;
    }

    started = true;
    manualStartPending = true;
    widget.start?.();
  };

  function initializeWidget() {
    if (widget || isDestroyed) {
      return;
    }

    const currentState = getState();

    if (hasUsableState(currentState)) {
      syncUiWithState(currentState);
      queueMicrotask(() => {
        options.onComplete?.();
      });
      return;
    }

    const markManualInteraction = () => {
      manualStartPending = true;
    };

    options.mountElement.addEventListener('pointerdown', markManualInteraction, { passive: true });
    options.mountElement.addEventListener('keydown', markManualInteraction);
    removeInteractionListeners = () => {
      options.mountElement.removeEventListener('pointerdown', markManualInteraction);
      options.mountElement.removeEventListener('keydown', markManualInteraction);
      manualStartPending = false;
    };

    widget = getSdk().createWidget({
      element: options.mountElement,
      sitekey: options.sitekey,
      language: options.locale,
      startMode: 'none'
    });

    widget.addEventListener('frc:widget.statechange', (event: any) => {
      const nextState = event?.detail?.state;

      if (nextState === 'activating' && !manualStartPending) {
        widget.reset?.({ trigger: 'root' });
        return;
      }

      if (nextState === 'activating' || nextState === 'activated' || nextState === 'requesting') {
        manualStartPending = false;
      }
    });

    widget.addEventListener('frc:widget.complete', (event: any) => {
      const token = event?.detail?.response;
      if (!token) {
        return;
      }

      saveToken(token);
      options.onComplete?.();
    });

    widget.addEventListener('frc:widget.expire', () => {
      const currentState = getState();

      if (hasUsableState(currentState)) {
        started = false;
        return;
      }

      clearState(storageKey);
      started = false;
      const nextState = getState();
      syncUiWithState(nextState);
      notifyStateChange(nextState);
      options.onExpire?.();
    });

    widget.addEventListener('frc:widget.error', (event: any) => {
      const currentState = getState();

      if (hasUsableState(currentState)) {
        started = false;
        return;
      }

      clearState(storageKey);
      started = false;
      const nextState = getState();
      syncUiWithState(nextState);
      notifyStateChange(nextState);
      options.onError?.(event?.detail?.error);
    });

  }

  return {
    attach() {
      if (widget || isDestroyed || attachRequested) {
        return;
      }

      attachRequested = true;
      scheduleInitialization();
    },
    startIfNeeded() {
      start();
    },
    getToken() {
      const state = getState();

      if (!hasUsableState(state)) {
        return undefined;
      }

      return state.token;
    },
    hasUsableToken() {
      return hasUsableState(getState());
    },
    markTokenUsed() {
      const state = getState();

      if (!state.token) {
        return;
      }

      thisFormUsedToken = true;
      const nextState = { ...state, used: true };
      writeState(storageKey, nextState);
      syncUiWithState(nextState);
      notifyStateChange(nextState);
    },
    reset() {
      clearState(storageKey);
      started = false;
      const nextState = getState();
      syncUiWithState(nextState);
      notifyStateChange(nextState);
    },
    destroy() {
      if (isDestroyed) {
        return;
      }

      isDestroyed = true;
      window.removeEventListener(STATE_EVENT_NAME, handleStateChange);
      removeModalShowListener();
      removeInteractionListeners();
      removeCachedTokenUI();
      widget?.destroy?.();
      widget = undefined;
    }
  };
}

export function initCaptchaForForm(options: InitCaptchaForFormOptions): InitCaptchaForFormResult {
  const { form, mountElement, emailInput } = options;

  if (!form || !mountElement) {
    return { requireCaptchaOnSubmit: false };
  }

  const captchaEnabled = options.enabled ?? isCaptchaEnabled();
  const controller = createCaptchaController({
    form,
    mountElement,
    emailInput,
    sitekey: options.sitekey || window.captain,
    locale: options.locale,
    onComplete: options.onComplete,
    onExpire: options.onExpire,
    onError: options.onError,
    onShowWidget: options.onShowWidget,
    onHideWidget: options.onHideWidget,
    enabled: captchaEnabled
  });

  controller.attach();

  return {
    controller,
    requireCaptchaOnSubmit: captchaEnabled
  };
}
